import type { BoVersion, GateIssue } from './types'

/** Where a version stands. A decline outranks an approval, so it is checked first. */
export type Verdict = 'pending' | 'approved' | 'declined'

export interface VersionRow {
  version: BoVersion
  isLive: boolean
  /** Publishing this one moves the pointer BACKWARDS - the same call, a different meaning. */
  isRollback: boolean
  verdict: Verdict
  approvedBy: string[]
  /** Who turned it down, and why. A decline outranks an approval. */
  declinedBy: { approver: string; note: string | null }[]
  blockedReason?: 'needsApproval' | 'alreadyLive' | 'declined'
}

export interface PublishView {
  rows: VersionRow[]
  live?: BoVersion
  hasNeverPublished: boolean
}

/**
 * The versions table, decided in one place.
 *
 * `published` comes from the channel pointer, so after a rollback a HIGHER version can be
 * unpublished. Everything here follows from that rather than from ordering.
 */
export function buildPublishView(versions: BoVersion[]): PublishView {
  const live = versions.find((v) => v.published)
  const rows = versions.map((version) => {
    const declinedBy = version.approvals
      .filter((a) => a.verdict === 'declined')
      .map((a) => ({ approver: a.approver, note: a.note }))
    const approvedBy = version.approvals
      .filter((a) => a.verdict !== 'declined')
      .map((a) => a.approver)
    const isLive = version.published
    const verdict: Verdict =
      declinedBy.length > 0
        ? 'declined'
        : approvedBy.length > 0
        ? 'approved'
        : 'pending'

    return {
      version,
      isLive,
      isRollback: Boolean(live && version.n < live.n && !isLive),
      verdict,
      approvedBy,
      declinedBy,
      // An approval is required to publish WHATEVER the server's flag says; whose approval is not
      // checked here. Four eyes (somebody other than the stager) is off until it comes back as a
      // configurable rule.
      blockedReason: isLive
        ? ('alreadyLive' as const)
        : verdict === 'declined'
        ? ('declined' as const)
        : approvedBy.length === 0
        ? ('needsApproval' as const)
        : undefined,
    }
  })

  return { rows, live, hasNeverPublished: !live }
}

export function canPublish(row: VersionRow): boolean {
  return row.blockedReason === undefined
}

/** A verdict is final: once a version is approved or declined, it is not voted on again. */
export function canApprove(row: VersionRow): boolean {
  return !row.isLive && row.verdict === 'pending'
}

/** Same rule as approving: the first verdict settles it, and a declined version is dead. */
export function canDecline(row: VersionRow): boolean {
  return !row.isLive && row.verdict === 'pending'
}

export interface GateGroup {
  code: string
  items: string[]
}

/** Gate issues grouped by rule, so seven rules read as seven problems and not forty lines. */
export function groupGateIssues(issues: GateIssue[]): GateGroup[] {
  const order: string[] = []
  const byCode = new Map<string, string[]>()

  issues.forEach((issue) => {
    if (!byCode.has(issue.code)) {
      byCode.set(issue.code, [])
      order.push(issue.code)
    }
    if (issue.detail) {
      byCode.get(issue.code)?.push(issue.detail)
    }
  })

  return order.map((code) => ({ code, items: byCode.get(code) ?? [] }))
}

export interface Baseline {
  n: number
  isLive: boolean
}

/**
 * What a version should be compared against: the LIVE one when reviewing something new — that is
 * what publishing would change — and the previous version otherwise. Undefined for the first
 * version of all, where everything is new.
 */
export function baselineFor(
  rows: VersionRow[],
  n: number,
): Baseline | undefined {
  const live = rows.find((r) => r.isLive)
  if (live && live.version.n !== n) {
    return { n: live.version.n, isLive: true }
  }
  const previous = rows
    .map((r) => r.version.n)
    .filter((m) => m < n)
    .sort((a, b) => b - a)[0]

  return previous === undefined ? undefined : { n: previous, isLive: false }
}
